import { create } from 'zustand'
import type { SourcePanelPosition } from '@/types/source'
import type { VoiceConversationSettings } from '@/types/voice'
import { DEFAULT_VOICE_CONVERSATION_SETTINGS } from '@/types/voice'

export type VoiceProviderType = 'elevenlabs' | 'azure'
export type AvatarProviderType = 'tavus' | 'anam'

// Re-export for convenience
export type { SourcePanelPosition }

/**
 * API credentials type — kept for backward compatibility with type exports.
 * Actual credential values are now stored in the backend only.
 * The settingsStore only tracks has*Key status flags.
 */
export interface ApiCredentials {
  elevenlabs?: string
  tavus?: string
  azureSpeech?: {
    apiKey: string
    region: string
  }
}

export interface ConnectionSettings {
  // Note: `ragServerUrl` was removed in Phase 5 of the rag-deployment-config
  // plan. The deployment-level RAG URL is now read from the feature manifest
  // (`featureStore.ragProxyBase`); see `useRagProxyBase()`.
  /** In-memory only: populated from backend credentials on startup */
  ragApiKey: string
  voiceProvider: VoiceProviderType
  avatarProvider: AvatarProviderType
}

/** Output format options for streaming TTS */
export type StreamingOutputFormat =
  | 'mp3_22050_32'
  | 'mp3_44100_64'
  | 'mp3_44100_96'
  | 'mp3_44100_128'
  | 'mp3_44100_192'
  | 'pcm_16000'
  | 'pcm_22050'
  | 'pcm_24000'
  | 'pcm_44100'
  | 'ulaw_8000'

export interface StreamingTTSConfig {
  /** Use global ElevenLabs endpoint for lower latency */
  useGlobalEndpoint: boolean
  /** Pre-buffer duration before starting playback (ms) */
  preBufferMs: number
  /** Default output format (can be overridden per persona) */
  defaultOutputFormat: StreamingOutputFormat
  /**
   * ElevenLabs auto_mode: synthesize whenever the incoming stream stalls.
   * Off by default — with phrase-trickled LLM streams it fragments
   * generation windows and pronunciation dictionaries apply unreliably.
   */
  autoMode: boolean
}

export interface VoiceFeatureSettings {
  /** Enable text-to-speech for assistant responses */
  ttsEnabled: boolean
  /** Enable speech-to-text for user input */
  sttEnabled: boolean
  /** Selected voice ID for TTS */
  selectedVoiceId: string | null
  /** Enable low-latency streaming TTS mode */
  useStreamingTts: boolean
  /** Streaming TTS configuration */
  streamingConfig: StreamingTTSConfig
  /**
   * Per-persona TTS toggle overrides.
   *
   * Chat-only personas (`presentationMode === 'chat'`) default TTS to OFF
   * regardless of the global `ttsEnabled`. The user can opt in (turn
   * the toggle on) and that choice is recorded here, keyed by persona id.
   * For voice/avatar personas the override is ignored — the global flag
   * is the source of truth. Persisted via the existing UserSettings
   * backend sync (settings_data JSON column; no schema change).
   */
  ttsPersonaOverrides: Record<string, boolean>
}

export interface UISettings {
  /** Position of the source panel */
  sourcePanelPosition: SourcePanelPosition
  /** Whether the source panel is collapsed */
  sourcePanelCollapsed: boolean
  /** Whether Inbox conversations are grouped by sender */
  inboxGroupBySender: boolean
  /** Which sender groups are collapsed in the Inbox (group keys) */
  inboxCollapsedGroups: string[]
  /** The Inbox record sidebar's width in px (SA-795); null = the default.
   * Read through `components/inbox/recordSidebarWidth.sidebarWidth`. */
  inboxRecordSidebarWidth: number | null
  /** The Inbox conversation list's width in px (SA-1079); null = the default.
   * Read through `components/inbox/listWidth.listWidth`. */
  inboxListWidth: number | null
  /** Resizable drawers' widths in px by drawer id (SA-813); absent = the
   * drawer's default. Read through `hooks/useDrawerWidth`. */
  drawerWidths: Record<string, number>
  /** How the Inbox shows a triage verdict note (SA-1090): today's box for
   * every note, one line, or hidden. Read through `components/inbox/noteMarker`. */
  inboxNotes: 'inline' | 'markers' | 'hidden'
  /** The marketing copilot speaks its replies (TTS). Off by default; persists. */
  copilotAutoSpeak: boolean
  /**
   * Per-user hidden personas, keyed by persona id (value always true).
   * Hidden personas are dropped from the Personas page and Home launcher
   * grid unless "Show hidden" is on. Persisted via the existing
   * UserSettings backend sync (settings_data JSON column; no schema change).
   */
  hiddenPersonaIds: Record<string, true>
  /**
   * Fleet Locator per-viewer prefs (custom surface, SA-321 §7.3): null layer
   * choices mean "use the deployment default from fleet settings". The core
   * must not deep-import custom code, so the shape is declared locally here
   * rather than importing `FleetPrefs` from `@/custom/fleet/types` (which is
   * structurally identical).
   */
  fleet: {
    dayImagery: 'blue_marble' | 'shaded_relief' | 'bathymetry' | null
    nightLights: 'city_lights' | 'black_marble' | 'off' | null
    sceneMode: '3D' | '2D'
    groups: string[]
    states: string[]
    /** Label layers (SA-325): GIBS place names / borders overlays and the airports layer. */
    labels: { placeNames: boolean; borders: boolean; airports: boolean }
    /** Aircraft family / series / model selections (SA-327); empty arrays = no filter. */
    typeFilter: { family: string[]; series: string[]; model: string[] }
  }
  /** Fleet Schedule per-viewer prefs (custom surface, SA-323 §7.7): nulls mean "use the deployment default". */
  fleetSchedule: {
    minTurnaroundHours: number | null
    weeksShown: 1 | 2 | 4 | null
    /** Days in view on the schedule strip (SA-1286); null = the default from weeksShown or the setting. */
    zoom: number | null
    timeDisplay: 'utc' | 'local' | null
    metricsOpen: boolean
    showChanges: boolean
    /** Same shape as `fleet.typeFilter`, kept separate so the two surfaces can differ (SA-327). */
    typeFilter: { family: string[]; series: string[]; model: string[] }
  }
  /**
   * Record-surface per-viewer prefs (SA-459, workflow UX sweep spec §3.5 /
   * D14): the record page's process band view (item B) and the register's
   * column widths by view id then column id (item D). Synced like `fleet`.
   */
  records: {
    /** The record page's process band: the map or the timeline (D5). */
    processView: 'map' | 'list'
    /** Register column widths in px, by view id (`register`, `pricing`, …) then column id. */
    columnWidths: Record<string, Record<string, number>>
  }
  /**
   * Home page per-viewer prefs (Home spec 2026-09-23 D3 / §3.4): the ordered
   * widgets with a width each (null = the deployment's initial layout) and
   * per-widget remembered choices by widget id (e.g. My work's scope). Synced
   * like `records`. The type is declared structurally because the core must
   * not deep-import the page; `pages/Home/types.ts` pins it at compile time.
   */
  home: {
    widgets: Array<{ id: string; width: 1 | 2 | 3 }> | null
    options: Record<string, Record<string, string>>
  }
  /**
   * Compass analytics per-viewer prefs (custom surface, SA-1113 §2.2, SA-1319): the Capacity strip's zoom in days in
   * view, read through the module's `zoomFor` so a value that is not a step reads as the nearest one; null until the
   * viewer zooms, when the strip shows the counted weeks' width. Synced like `records`.
   */
  analytics: { capacityZoomDays: number | null }
}

interface SettingsState {
  // Connection settings
  connections: ConnectionSettings

  // Voice feature settings
  voiceFeatures: VoiceFeatureSettings

  // Voice conversation settings (full-duplex)
  voiceConversation: VoiceConversationSettings

  // UI settings
  ui: UISettings

  // Track which credentials are set (status only — values in backend)
  hasElevenLabsKey: boolean
  hasTavusKey: boolean
  hasAnamKey: boolean
  hasAzureKey: boolean
  hasRagApiKey: boolean
  hasDeepgramKey: boolean
  hasOpenAIKey: boolean
  hasStripeKey: boolean

  // Credential metadata: tracks source and admin-managed status per service
  credentialMeta: Record<string, {
    source: 'direct' | 'credential_set'
    isAdminManaged: boolean
    credentialSetName?: string
    setByName?: string
  }>

  // In-memory credential cache (populated from backend, never persisted)
  _credentialCache: {
    elevenlabs?: string
    tavus?: string
    azureSpeech?: { apiKey: string; region: string }
  }

  // The viewer's settings are known as far as they will be: read from the backend, or settled on the defaults after a
  // read that failed twice (SA-1301). The strips' first placement waits on it.
  _settingsLoaded: boolean
  // The settings came from the server, so the sync may save (SA-1301). Never true after a failed read: saving the
  // defaults would replace every stored section, because the PUT merges one level deep.
  _settingsSaveable: boolean

  // Actions - Connection settings
  // setRagServerUrl removed in Phase 5 — see ConnectionSettings comment.
  setRagApiKey: (key: string) => void
  clearRagApiKey: () => void
  getRagApiKey: () => string
  setVoiceProvider: (provider: VoiceProviderType) => void
  setAvatarProvider: (provider: AvatarProviderType) => void

  // Actions - Voice feature settings
  setTtsEnabled: (enabled: boolean) => void
  setSttEnabled: (enabled: boolean) => void
  setSelectedVoiceId: (voiceId: string | null) => void
  setUseStreamingTts: (enabled: boolean) => void
  setStreamingConfig: (config: Partial<StreamingTTSConfig>) => void
  /** Set the per-persona TTS override (chat-only personas only). */
  setTtsPersonaOverride: (personaId: string, enabled: boolean) => void
  /** Remove a per-persona TTS override (revert to default-for-mode). */
  clearTtsPersonaOverride: (personaId: string) => void

  // Actions - Voice conversation settings
  setVoiceConversation: (settings: Partial<VoiceConversationSettings>) => void

  // Actions - UI settings
  setSourcePanelPosition: (position: SourcePanelPosition) => void
  setSourcePanelCollapsed: (collapsed: boolean) => void
  setInboxGroupBySender: (enabled: boolean) => void
  setInboxCollapsedGroups: (groups: string[]) => void
  setInboxRecordSidebarWidth: (width: number | null) => void
  setInboxListWidth: (width: number | null) => void
  setInboxNotes: (mode: 'inline' | 'markers' | 'hidden') => void
  /** Store a resizable drawer's width, or forget it (null = the default). */
  setDrawerWidth: (id: string, width: number | null) => void
  setCopilotAutoSpeak: (enabled: boolean) => void
  /** Hide a persona from this user's browse surfaces (per-user, cosmetic). */
  hidePersona: (personaId: string) => void
  /** Remove a persona from this user's hidden set. */
  unhidePersona: (personaId: string) => void
  /** Patch the Fleet Locator per-viewer prefs. */
  setFleetPrefs: (patch: Partial<UISettings['fleet']>) => void
  /** Patch the Fleet Schedule per-viewer prefs. */
  setFleetSchedulePrefs: (patch: Partial<UISettings['fleetSchedule']>) => void
  /** Patch the record-surface per-viewer prefs (SA-459). */
  setRecordsPrefs: (patch: Partial<UISettings['records']>) => void
  /** Patch the Home page per-viewer prefs (Home spec 2026-09-23). */
  setHomePrefs: (patch: Partial<UISettings['home']>) => void
  /** Patch the Compass analytics per-viewer prefs (SA-1113). */
  setAnalyticsPrefs: (patch: Partial<UISettings['analytics']>) => void

  // Actions - Credential status (values stored in backend only)
  setCredentialStatus: (service: 'elevenlabs' | 'tavus' | 'anam' | 'azure' | 'rag' | 'deepgram' | 'openai' | 'stripe', hasKey: boolean) => void
  clearAllCredentialStatus: () => void
  setCredentialMeta: (meta: Record<string, { source: 'direct' | 'credential_set'; isAdminManaged: boolean; credentialSetName?: string; setByName?: string }>) => void

  // Credential cache setters/getters (in-memory only, populated from backend)
  cacheCredential: (service: 'elevenlabs' | 'tavus', key: string) => void
  cacheAzureCredential: (apiKey: string, region: string) => void
  clearCredentialCache: () => void
  getElevenLabsKey: () => string | undefined
  getTavusKey: () => string | undefined
  getAzureCredentials: () => { apiKey: string; region: string } | undefined

  // Actions - Backend sync
  loadSettings: (data: {
    connections?: Partial<Omit<ConnectionSettings, 'ragApiKey'>>
    voiceFeatures?: Partial<VoiceFeatureSettings>
    voiceConversation?: Partial<VoiceConversationSettings>
    ui?: Partial<UISettings>
  }) => void
  // The settings could not be read: known as the defaults, never saveable (SA-1301). Merges nothing.
  settleWithoutSettings: () => void
  getSettingsForSync: () => {
    connections: Omit<ConnectionSettings, 'ragApiKey'>
    voiceFeatures: VoiceFeatureSettings
    voiceConversation: VoiceConversationSettings
    ui: UISettings
  }
}

/** Preferences a retired feature left in synced blobs: Ask Doc's three (SA-1306). Dropped on
 * load, so the next save no longer carries them. */
export const RETIRED_UI_KEYS = ['docAssistantEnabled', 'docAutoSpeak', 'docVoiceAutoSend'] as const

/** The tail canvas's zoom multiplier (SA-1113), which the strip's days in view replaced (SA-1319): dropped on load, never
 * converted, so a viewer who zoomed the canvas starts the strip never zoomed. */
function withoutCanvasZoom(analytics: object): UISettings['analytics'] {
  const out: Record<string, unknown> = { ...analytics }
  delete out.capacityZoom
  return out as UISettings['analytics']
}

function withoutRetiredUiKeys(ui: Partial<UISettings> | undefined): Partial<UISettings> {
  const out: Record<string, unknown> = { ...(ui || {}) }
  for (const key of RETIRED_UI_KEYS) delete out[key]
  return out as Partial<UISettings>
}

export const useSettingsStore = create<SettingsState>()(
  (set, get) => ({
    // Default connection settings
    connections: {
      ragApiKey: '',
      voiceProvider: 'elevenlabs',
      avatarProvider: 'tavus',
    },

    // Default voice feature settings
    voiceFeatures: {
      ttsEnabled: false,
      sttEnabled: false,
      selectedVoiceId: null,
      useStreamingTts: true,
      streamingConfig: {
        useGlobalEndpoint: true,
        preBufferMs: 100,
        defaultOutputFormat: 'pcm_22050',
        autoMode: false,
      },
      ttsPersonaOverrides: {},
    },

    // Default voice conversation settings (full-duplex)
    voiceConversation: { ...DEFAULT_VOICE_CONVERSATION_SETTINGS },

    // Default UI settings
    ui: {
      sourcePanelPosition: 'right',
      sourcePanelCollapsed: false,
      inboxGroupBySender: false,
      inboxCollapsedGroups: [],
      inboxRecordSidebarWidth: null,
      inboxListWidth: null,
      inboxNotes: 'markers',
      drawerWidths: {},
      copilotAutoSpeak: false,
      hiddenPersonaIds: {},
      fleet: { dayImagery: null, nightLights: null, sceneMode: '3D', groups: [], states: [], labels: { placeNames: false, borders: false, airports: false }, typeFilter: { family: [], series: [], model: [] } },
      fleetSchedule: { minTurnaroundHours: null, weeksShown: null, zoom: null, timeDisplay: null, metricsOpen: true, showChanges: true, typeFilter: { family: [], series: [], model: [] } },
      records: { processView: 'map', columnWidths: {} },
      home: { widgets: null, options: {} },
      analytics: { capacityZoomDays: null },
    },

    // Credential status flags (populated from backend on startup)
    hasElevenLabsKey: false,
    hasTavusKey: false,
    hasAnamKey: false,
    hasAzureKey: false,
    hasRagApiKey: false,
    hasDeepgramKey: false,
    hasOpenAIKey: false,
    hasStripeKey: false,

    // Credential metadata (source/admin tracking)
    credentialMeta: {},

    // In-memory credential cache
    _credentialCache: {},

    // Settings loaded and saveable flags
    _settingsLoaded: false,
    _settingsSaveable: false,

    // Connection setters
    // setRagServerUrl removed in Phase 5 — see ConnectionSettings comment.

    setRagApiKey: (key) =>
      set((state) => ({
        connections: { ...state.connections, ragApiKey: key },
        hasRagApiKey: key.length > 0,
      })),

    clearRagApiKey: () =>
      set((state) => ({
        connections: { ...state.connections, ragApiKey: '' },
        hasRagApiKey: false,
      })),

    getRagApiKey: () => get().connections.ragApiKey,

    setVoiceProvider: (provider) =>
      set((state) => ({
        connections: { ...state.connections, voiceProvider: provider },
      })),

    setAvatarProvider: (provider) =>
      set((state) => ({
        connections: { ...state.connections, avatarProvider: provider },
      })),

    // Voice feature setters
    setTtsEnabled: (enabled) =>
      set((state) => ({
        voiceFeatures: { ...state.voiceFeatures, ttsEnabled: enabled },
      })),

    setSttEnabled: (enabled) =>
      set((state) => ({
        voiceFeatures: { ...state.voiceFeatures, sttEnabled: enabled },
      })),

    setSelectedVoiceId: (voiceId) =>
      set((state) => ({
        voiceFeatures: { ...state.voiceFeatures, selectedVoiceId: voiceId },
      })),

    setUseStreamingTts: (enabled) =>
      set((state) => ({
        voiceFeatures: { ...state.voiceFeatures, useStreamingTts: enabled },
      })),

    setTtsPersonaOverride: (personaId, enabled) =>
      set((state) => ({
        voiceFeatures: {
          ...state.voiceFeatures,
          ttsPersonaOverrides: {
            ...state.voiceFeatures.ttsPersonaOverrides,
            [personaId]: enabled,
          },
        },
      })),

    clearTtsPersonaOverride: (personaId) =>
      set((state) => {
        const { [personaId]: _removed, ...rest } =
          state.voiceFeatures.ttsPersonaOverrides
        return {
          voiceFeatures: { ...state.voiceFeatures, ttsPersonaOverrides: rest },
        }
      }),

    setStreamingConfig: (config) =>
      set((state) => ({
        voiceFeatures: {
          ...state.voiceFeatures,
          streamingConfig: {
            ...state.voiceFeatures.streamingConfig,
            ...config,
          },
        },
      })),

    // Voice conversation setters
    setVoiceConversation: (settings) =>
      set((state) => ({
        voiceConversation: {
          ...state.voiceConversation,
          ...settings,
          models: {
            ...state.voiceConversation.models,
            ...(settings.models || {}),
          },
          realtimeSettings: {
            ...state.voiceConversation.realtimeSettings,
            ...(settings.realtimeSettings || {}),
          },
        },
      })),

    // UI setters
    setSourcePanelPosition: (position) =>
      set((state) => ({
        ui: { ...state.ui, sourcePanelPosition: position },
      })),

    setSourcePanelCollapsed: (collapsed) =>
      set((state) => ({
        ui: { ...state.ui, sourcePanelCollapsed: collapsed },
      })),

    setInboxGroupBySender: (enabled) =>
      set((state) => ({
        ui: { ...state.ui, inboxGroupBySender: enabled },
      })),

    setInboxCollapsedGroups: (groups) =>
      set((state) => ({
        ui: { ...state.ui, inboxCollapsedGroups: groups },
      })),

    setInboxRecordSidebarWidth: (width) =>
      set((state) => ({ ui: { ...state.ui, inboxRecordSidebarWidth: width } })),
    setInboxListWidth: (width) =>
      set((state) => ({ ui: { ...state.ui, inboxListWidth: width } })),
    setInboxNotes: (mode) =>
      set((state) => ({ ui: { ...state.ui, inboxNotes: mode } })),

    setDrawerWidth: (id, width) =>
      set((state) => {
        const drawerWidths = { ...state.ui.drawerWidths }
        if (width === null) delete drawerWidths[id]
        else drawerWidths[id] = width
        return { ui: { ...state.ui, drawerWidths } }
      }),

    setCopilotAutoSpeak: (enabled) =>
      set((state) => ({ ui: { ...state.ui, copilotAutoSpeak: enabled } })),

    setFleetPrefs: (patch) =>
      set((state) => ({ ui: { ...state.ui, fleet: { ...state.ui.fleet, ...patch } } })),

    setFleetSchedulePrefs: (patch) =>
      set((state) => ({ ui: { ...state.ui, fleetSchedule: { ...state.ui.fleetSchedule, ...patch } } })),

    setRecordsPrefs: (patch) =>
      set((state) => ({ ui: { ...state.ui, records: { ...state.ui.records, ...patch } } })),

    setHomePrefs: (patch) =>
      set((state) => ({ ui: { ...state.ui, home: { ...state.ui.home, ...patch } } })),
    setAnalyticsPrefs: (patch) =>
      set((state) => ({ ui: { ...state.ui, analytics: { ...state.ui.analytics, ...patch } } })),

    hidePersona: (personaId) =>
      set((state) => ({
        ui: {
          ...state.ui,
          hiddenPersonaIds: { ...state.ui.hiddenPersonaIds, [personaId]: true },
        },
      })),

    unhidePersona: (personaId) =>
      set((state) => {
        // No-op for ids that aren't hidden: a fresh `ui` reference here
        // would trigger a needless settings-sync PUT.
        if (!(personaId in state.ui.hiddenPersonaIds)) return state
        const { [personaId]: _removed, ...rest } = state.ui.hiddenPersonaIds
        return { ui: { ...state.ui, hiddenPersonaIds: rest } }
      }),

    // Credential status setters (no actual values stored)
    setCredentialStatus: (service, hasKey) => {
      switch (service) {
        case 'elevenlabs':
          set({ hasElevenLabsKey: hasKey })
          break
        case 'tavus':
          set({ hasTavusKey: hasKey })
          break
        case 'anam':
          set({ hasAnamKey: hasKey })
          break
        case 'azure':
          set({ hasAzureKey: hasKey })
          break
        case 'rag':
          set({ hasRagApiKey: hasKey })
          break
        case 'deepgram':
          set({ hasDeepgramKey: hasKey })
          break
        case 'openai':
          set({ hasOpenAIKey: hasKey })
          break
        case 'stripe':
          set({ hasStripeKey: hasKey })
          break
      }
    },

    clearAllCredentialStatus: () =>
      set({
        hasElevenLabsKey: false,
        hasTavusKey: false,
        hasAnamKey: false,
        hasAzureKey: false,
        hasRagApiKey: false,
        hasDeepgramKey: false,
        hasOpenAIKey: false,
        hasStripeKey: false,
        credentialMeta: {},
      }),

    setCredentialMeta: (meta) => set({ credentialMeta: meta }),

    // Credential cache methods (in-memory only)
    cacheCredential: (service, key) =>
      set((state) => ({
        _credentialCache: { ...state._credentialCache, [service]: key },
      })),

    cacheAzureCredential: (apiKey, region) =>
      set((state) => ({
        _credentialCache: { ...state._credentialCache, azureSpeech: { apiKey, region } },
      })),

    clearCredentialCache: () => set({ _credentialCache: {} }),

    getElevenLabsKey: () => get()._credentialCache.elevenlabs,
    getTavusKey: () => get()._credentialCache.tavus,
    getAzureCredentials: () => get()._credentialCache.azureSpeech,

    // Backend sync: populate store from backend settings
    loadSettings: (data) => {
      const state = get()
      console.debug('[SettingsStore] loadSettings called with:', {
        incoming: data.voiceFeatures,
        current: state.voiceFeatures,
      })
      set({
        connections: {
          ...state.connections,
          // ragServerUrl removed in Phase 5 — legacy values in saved settings
          // are silently ignored on read. No DB migration needed.
          voiceProvider: data.connections?.voiceProvider ?? state.connections.voiceProvider,
          avatarProvider: data.connections?.avatarProvider ?? state.connections.avatarProvider,
        },
        voiceFeatures: {
          ...state.voiceFeatures,
          ...(data.voiceFeatures || {}),
          streamingConfig: {
            ...state.voiceFeatures.streamingConfig,
            ...(data.voiceFeatures?.streamingConfig || {}),
          },
        },
        voiceConversation: {
          ...state.voiceConversation,
          ...(data.voiceConversation || {}),
          models: {
            ...state.voiceConversation.models,
            ...(data.voiceConversation?.models || {}),
          },
          realtimeSettings: {
            ...state.voiceConversation.realtimeSettings,
            ...(data.voiceConversation?.realtimeSettings || {}),
          },
        },
        ui: {
          ...state.ui,
          ...withoutRetiredUiKeys(data.ui),
          fleet: {
            ...state.ui.fleet,
            ...(data.ui?.fleet || {}),
            // Nested merge (SA-325): a blob synced before the label toggles existed,
            // or a partial one, must not hydrate `labels` as undefined or drop a key.
            labels: { ...state.ui.fleet.labels, ...(data.ui?.fleet?.labels || {}) },
            // Nested merge (SA-327): a blob that predates the type filter, or a partial one.
            typeFilter: { ...state.ui.fleet.typeFilter, ...(data.ui?.fleet?.typeFilter || {}) },
          },
          fleetSchedule: {
            ...state.ui.fleetSchedule,
            ...(data.ui?.fleetSchedule || {}),
            typeFilter: { ...state.ui.fleetSchedule.typeFilter, ...(data.ui?.fleetSchedule?.typeFilter || {}) },
          },
          records: {
            ...state.ui.records,
            ...(data.ui?.records || {}),
            // Nested merge (SA-459): a blob synced before the record prefs
            // existed, or a partial one, must not hydrate a key as undefined.
            columnWidths: { ...state.ui.records.columnWidths, ...(data.ui?.records?.columnWidths || {}) },
          },
          // Nested merge (SA-813): a blob that predates the key, or a null one,
          // must not hydrate `drawerWidths` as undefined.
          drawerWidths: { ...state.ui.drawerWidths, ...(data.ui?.drawerWidths || {}) },
          home: {
            ...state.ui.home,
            ...(data.ui?.home || {}),
            // Nested merge: a blob synced before the Home prefs existed, or a
            // partial one, must not hydrate `options` as undefined.
            options: { ...state.ui.home.options, ...(data.ui?.home?.options || {}) },
          },
          // A blob synced before the analytics prefs existed, or a partial one, keeps the default zoom; the canvas's
          // multiplier is dropped.
          analytics: withoutCanvasZoom({ ...state.ui.analytics, ...(data.ui?.analytics || {}) }),
        },
        _settingsLoaded: true,
        _settingsSaveable: true,
      })
      console.debug('[SettingsStore] loadSettings result:', {
        useStreamingTts: get().voiceFeatures.useStreamingTts,
      })
    },

    settleWithoutSettings: () => set({ _settingsLoaded: true }),

    // Get settings for syncing to backend (excludes runtime-only fields).
    // Note: ragServerUrl removed in Phase 5 — no longer synced.
    getSettingsForSync: () => {
      const { connections, voiceFeatures, voiceConversation, ui } = get()
      return {
        connections: {
          voiceProvider: connections.voiceProvider,
          avatarProvider: connections.avatarProvider,
        },
        voiceFeatures,
        voiceConversation,
        ui,
      }
    },
  })
)
